import { Star, Quote, BadgeCheck, ThumbsUp, ThumbsDown, MoreVertical } from "lucide-react";
import { Reveal } from "./Reveal";
import { TESTIMONIALS, PROOF_SHOTS, COMMUNITY_REVIEWS } from "@/data/content";

const AVATAR_COLORS = [
  "bg-brand-green/15 text-brand-green",
  "bg-brand-blue/15 text-brand-blue",
  "bg-brand-magenta/15 text-brand-magenta",
];

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} von 5 Sternen`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={i < n ? "h-4 w-4 fill-brand-green text-brand-green" : "h-4 w-4 text-white/20"}
      />
    ))}
  </div>
);

export const Testimonials = () => {
  return (
    <section id="stimmen" data-testid="testimonials" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(85%_55%_at_50%_0%,rgba(30,155,255,0.08),transparent_55%)]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <span className="font-heading text-xs font-semibold uppercase tracking-[0.24em] text-brand-blue">
              Teilnehmerstimmen
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Das sagen andere <span className="text-gradient-brand">Teilnehmer.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2">
              <Stars n={5} />
              <span className="font-heading text-sm font-semibold text-white">4,9 / 5</span>
              <span className="font-body text-sm text-white/50">· Feedback von Webinar-Teilnehmern</span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((r, i) => (
            <Reveal key={r.name} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`testimonial-${i}`}
                className="group relative flex h-full flex-col rounded-2xl border border-white/10 bg-brand-surface p-7 transition-colors duration-300 hover:border-brand-green/40 sm:p-8"
              >
                <Quote className="absolute right-6 top-6 h-8 w-8 text-white/5 transition-colors duration-300 group-hover:text-brand-green/20" />
                <Stars n={r.rating} />
                <blockquote className="mt-4 flex-1 font-body text-sm leading-relaxed text-white/75 sm:text-base">
                  „{r.text}"
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                  >
                    {r.initials}
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-bold text-white">{r.name}</span>
                    <span className="block font-body text-xs text-white/45">Kurier Connect · Teilnehmer</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.05}>
          <div className="mt-16">
            <div className="mb-8 flex items-center justify-center gap-2 text-center">
              <BadgeCheck className="h-5 w-5 shrink-0 text-brand-green" />
              <span className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
                Echte Nachrichten unserer Teilnehmer
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {PROOF_SHOTS.map((p, i) => (
                <a
                  key={p.src}
                  href={p.src}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`proof-shot-${i}`}
                  className="group block overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-2 transition-colors duration-300 hover:border-brand-green/40"
                >
                  <img
                    src={p.src}
                    alt={`WhatsApp-Nachricht von ${p.name}`}
                    loading="lazy"
                    className="mx-auto h-[300px] w-full rounded-lg object-contain transition-transform duration-300 group-hover:scale-[1.02] sm:h-[360px]"
                  />
                </a>
              ))}
            </div>

            <div className="mt-8">
              <div className="mb-6 text-center font-heading text-sm font-semibold uppercase tracking-[0.2em] text-white/50">
                Weitere Stimmen aus der Community
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {COMMUNITY_REVIEWS.map((r, i) => {
                  const initial = r.handle.replace("@", "").charAt(0).toUpperCase();
                  return (
                    <div
                      key={i}
                      data-testid={`community-review-${i}`}
                      className="flex h-full flex-col rounded-xl border border-white/10 bg-brand-surface p-4"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-xs font-bold ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                          {initial}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-heading text-xs font-bold text-white">{r.handle}</p>
                          <p className="font-body text-[10px] text-white/40">{r.when}</p>
                        </div>
                        <MoreVertical className="ml-auto h-4 w-4 shrink-0 text-white/25" />
                      </div>
                      <p className="mt-3 flex-1 font-body text-sm leading-relaxed text-white/75">{r.text}</p>
                      <div className="mt-4 flex items-center gap-4 text-white/40">
                        <span className="inline-flex items-center gap-1.5 font-body text-xs">
                          <ThumbsUp className="h-3.5 w-3.5" /> {r.likes}
                        </span>
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-8 text-center font-body text-xs text-white/35">
            Einzelne Erfahrungsberichte. Ergebnisse und Eignung können individuell abweichen.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
